import type { JournalEntryLineBadge } from '@/components/JournalEntryPreview'
import type { JournalEntryLine } from '@/services/accounting'
import type { AssetBreakdownRow, ExpenseType } from '@/services/fixedAssets'
import { parseMoney } from './fixedAssetsFormat'

export type FaLine = JournalEntryLine & { section?: string | null }

export type AllocateSummary = {
  totalDepreciation: number
  totalAmortization: number
  grandTotal: number
  contributingAssetCount: number
  trueUpTotal: number
}

const TRUE_UP_BADGE: JournalEntryLineBadge = {
  label: 'includes true-up',
  tone: 'amber',
}

function rowAmount(row: AssetBreakdownRow): number {
  if (row.amount != null && row.amount !== '') {
    return parseMoney(row.amount)
  }
  return parseMoney(row.expense) + parseMoney(row.true_up)
}

export function hasNonzeroMonthExpense(row: AssetBreakdownRow): boolean {
  return Math.abs(parseMoney(row.expense)) > 0.005
}

export function rowsWithMonthExpense(rows: AssetBreakdownRow[]): AssetBreakdownRow[] {
  return rows.filter(hasNonzeroMonthExpense)
}

export function expenseTypeToSection(expenseType: ExpenseType): string {
  return expenseType === 'depreciation' ? 'Depreciation' : 'Amortization'
}

export function buildLineSections(lines: FaLine[]): string[] | undefined {
  const sections = lines.map((line) => line.section ?? '')
  return sections.some((section) => section.trim().length > 0) ? sections : undefined
}

const SECTION_DISPLAY_ORDER = ['Depreciation', 'Amortization'] as const

export function orderLinesBySection(lines: FaLine[]): FaLine[] {
  const otherSections: string[] = []
  for (const line of lines) {
    const section = (line.section ?? '').trim()
    if (
      section &&
      !(SECTION_DISPLAY_ORDER as readonly string[]).includes(section) &&
      !otherSections.includes(section)
    ) {
      otherSections.push(section)
    }
  }

  const groupOrder = [...SECTION_DISPLAY_ORDER, ...otherSections, '']

  function groupIndex(line: FaLine): number {
    const section = (line.section ?? '').trim()
    if (!section) return groupOrder.length - 1
    const index = groupOrder.indexOf(section)
    return index >= 0 ? index : groupOrder.length - 1
  }

  return [...lines].sort((left, right) => groupIndex(left) - groupIndex(right))
}

export function buildLineBadges(
  lines: FaLine[],
  assetBreakdown: AssetBreakdownRow[],
): Record<number, JournalEntryLineBadge> {
  const trueUpRows = assetBreakdown.filter(
    (row) => Math.abs(parseMoney(row.true_up)) > 0.005,
  )
  if (trueUpRows.length === 0) return {}

  const badges: Record<number, JournalEntryLineBadge> = {}
  const sectionsWithTrueUp = new Set<string>()

  for (const row of trueUpRows) {
    sectionsWithTrueUp.add(expenseTypeToSection(row.expense_type))
  }

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index]
    if (!line) continue

    const section = (line.section ?? '').trim()
    if (!section || !sectionsWithTrueUp.has(section)) continue

    if (line.posting_type === 'Credit') {
      badges[index] = TRUE_UP_BADGE
    }
  }

  const matchedDebitIndices = new Set<number>()

  for (const row of trueUpRows) {
    const section = expenseTypeToSection(row.expense_type)
    const location = row.location.trim()
    const expectedAmount = rowAmount(row)
    const name = row.name

    for (let index = 0; index < lines.length; index += 1) {
      const line = lines[index]
      if (!line || line.posting_type !== 'Debit') continue
      if ((line.section ?? '').trim() !== section) continue
      if ((line.department_name ?? '').trim() !== location) continue
      const description = line.description ?? ''
      if (!description.includes(name)) continue
      if (Math.abs(parseMoney(line.amount) - expectedAmount) > 0.005) continue

      badges[index] = TRUE_UP_BADGE
      matchedDebitIndices.add(index)
    }
  }

  for (const section of sectionsWithTrueUp) {
    const sectionLineIndices = lines
      .map((line, index) => ((line.section ?? '').trim() === section ? index : -1))
      .filter((index) => index >= 0)

    if (sectionLineIndices.length === 0) continue

    const trueUpRowsInSection = trueUpRows.filter(
      (row) => expenseTypeToSection(row.expense_type) === section,
    )

    const debitsWithTrueUp = sectionLineIndices.filter((index) => {
      const line = lines[index]
      return line?.posting_type === 'Debit' && matchedDebitIndices.has(index)
    })

    const ambiguous =
      debitsWithTrueUp.length < trueUpRowsInSection.length ||
      debitsWithTrueUp.some((index) => !badges[index])

    if (ambiguous) {
      for (const index of sectionLineIndices) {
        badges[index] = TRUE_UP_BADGE
      }
    }
  }

  return badges
}

export function computeAllocateSummary(breakdown: AssetBreakdownRow[]): AllocateSummary {
  let totalDepreciation = 0
  let totalAmortization = 0
  let trueUpTotal = 0
  let contributingAssetCount = 0

  for (const row of breakdown) {
    const expense = parseMoney(row.expense)
    const trueUp = parseMoney(row.true_up)
    const amount = rowAmount(row)

    trueUpTotal += trueUp

    if (Math.abs(amount) > 0.005) {
      contributingAssetCount += 1
    }

    if (row.expense_type === 'depreciation') {
      totalDepreciation += expense
    } else {
      totalAmortization += expense
    }
  }

  return {
    totalDepreciation,
    totalAmortization,
    grandTotal: totalDepreciation + totalAmortization + trueUpTotal,
    contributingAssetCount,
    trueUpTotal,
  }
}

export function isZeroExpenseMonth(hint: string | null | undefined, summary: AllocateSummary): boolean {
  if (hint === 'No expense') return true
  return summary.contributingAssetCount === 0 && Math.abs(summary.grandTotal) < 0.005
}

/** True when some Debit line lacks both department_id and a trimmed department_name. */
export function debitLinesMissingLocation(lines: JournalEntryLine[]): boolean {
  return lines.some(
    (line) =>
      line.posting_type === 'Debit' &&
      !line.department_id &&
      !line.department_name?.trim(),
  )
}
